export const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
export const MAX_IMAGE_PIXELS = 16_000_000;
export const SUPPORTED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];

export class ImageProcessingError extends Error {
  constructor(code) {
    super(code);
    this.name = "ImageProcessingError";
    this.code = code;
  }
}

export function imageProcessingMessage(error, italian = true) {
  const messages = {
    unsupported: ["Formato immagine non supportato. Usa JPEG, PNG o WebP.", "Unsupported image format. Use JPEG, PNG or WebP."],
    too_many_pixels: ["Immagine troppo grande. Ridimensionala a massimo 16 megapixel e riprova.", "Image is too large. Resize it to a maximum of 16 megapixels and try again."],
    too_large: ["File troppo pesante. Usa un’immagine fino a 10 MB.", "File is too large. Use an image up to 10 MB."],
    invalid: ["Impossibile leggere l’immagine. Verifica il file e riprova.", "Unable to read the image. Check the file and try again."],
  };
  const message = messages[error?.code];
  return message ? message[italian ? 0 : 1] : "";
}

function decodeImage(file) {
  if (typeof createImageBitmap === "function") return createImageBitmap(file);
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => { URL.revokeObjectURL(url); resolve(image); };
    image.onerror = () => { URL.revokeObjectURL(url); reject(new ImageProcessingError("invalid")); };
    image.src = url;
  });
}

function canvasBlob(canvas, type, quality) {
  return new Promise((resolve, reject) => {
    try {
      canvas.toBlob(blob => blob ? resolve(blob) : reject(new ImageProcessingError("invalid")), type, quality);
    } catch {
      reject(new ImageProcessingError("invalid"));
    }
  });
}

function readAsDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new ImageProcessingError("invalid"));
    reader.readAsDataURL(blob);
  });
}

function containsTransparency(context, width, height, sourceType) {
  if (sourceType === "image/jpeg") return false;
  const alpha = context.getImageData(0, 0, width, height).data;
  for (let index = 3; index < alpha.length; index += 4) {
    if (alpha[index] < 255) return true;
  }
  return false;
}

/**
 * Decode, downscale and re-encode a local photo before it becomes a Data URL.
 * Re-encoding through canvas strips the original metadata, including GPS EXIF.
 */
export async function prepareImageDataUrl(file, { maxLongEdge = 1600 } = {}) {
  if (!file || !SUPPORTED_IMAGE_TYPES.includes(file.type)) throw new ImageProcessingError("unsupported");
  if (!Number.isFinite(file.size) || file.size > MAX_IMAGE_BYTES) throw new ImageProcessingError("too_large");
  if (!Number.isFinite(maxLongEdge) || maxLongEdge < 1) throw new ImageProcessingError("invalid");

  let bitmap;
  try {
    bitmap = await decodeImage(file);
  } catch (error) {
    if (error instanceof ImageProcessingError) throw error;
    throw new ImageProcessingError("invalid");
  }

  try {
    const sourceWidth = bitmap.width || bitmap.naturalWidth;
    const sourceHeight = bitmap.height || bitmap.naturalHeight;
    if (!sourceWidth || !sourceHeight) throw new ImageProcessingError("invalid");
    if (sourceWidth * sourceHeight > MAX_IMAGE_PIXELS) throw new ImageProcessingError("too_many_pixels");

    const initialScale = Math.min(1, maxLongEdge / Math.max(sourceWidth, sourceHeight));
    let width = Math.max(1, Math.round(sourceWidth * initialScale));
    let height = Math.max(1, Math.round(sourceHeight * initialScale));
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d", { willReadFrequently: true });
    if (!context) throw new ImageProcessingError("invalid");
    canvas.width = width;
    canvas.height = height;
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = "high";
    context.drawImage(bitmap, 0, 0, width, height);

    let transparent = false;
    try {
      transparent = containsTransparency(context, width, height, file.type);
    } catch {
      throw new ImageProcessingError("invalid");
    }

    const formats = transparent
      ? [{ type: "image/webp", qualities: [0.9, 0.82, 0.72] }, { type: "image/png", qualities: [undefined] }]
      : [{ type: "image/jpeg", qualities: [0.86, 0.78, 0.68, 0.58] }];

    for (let resizeAttempt = 0; resizeAttempt < 9; resizeAttempt += 1) {
      for (const format of formats) {
        for (const quality of format.qualities) {
          const blob = await canvasBlob(canvas, format.type, quality);
          if ((!transparent && blob.type !== "image/jpeg") || (transparent && !["image/webp", "image/png"].includes(blob.type))) throw new ImageProcessingError("invalid");
          if (blob.size <= MAX_IMAGE_BYTES) return await readAsDataUrl(blob);
        }
      }
      width = Math.max(1, Math.floor(width * 0.82));
      height = Math.max(1, Math.floor(height * 0.82));
      canvas.width = width;
      canvas.height = height;
      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = "high";
      context.drawImage(bitmap, 0, 0, width, height);
    }

    throw new ImageProcessingError("too_large");
  } finally {
    if (typeof bitmap.close === "function") bitmap.close();
  }
}
